import { ButtonHTMLAttributes, CSSProperties, FC, ReactNode, useEffect, useState } from "react";
import { clsx } from "clsx";
import { SvgIcon } from "../../../common/components/SvgIcon";
import { Segmented } from "../Segmented";
import { SearchInput } from "../SearchInput";
import { Tooltip } from "../Tooltip";
import {
    ColumnFilters,
    ColumnsMenu,
    DataTableActiveFilters,
    DataTableFilterSpec,
    FilterableColumn,
    passesFilters,
} from "./DataTableFilters";
import classes from "./DataTable.module.less";

export type { DataTableFilterSpec, DataTableActiveFilters } from "./DataTableFilters";

export type DataTableSortType = "text" | "number" | "date";
export type DataTableSortDirection = "ascending" | "descending";

export interface DataTableColumn<T> {
    key: string;
    title: string;
    render: (row: T) => ReactNode;
    sort?: DataTableSortType;
    sortValue?: (row: T) => string | number | Date | null | undefined;
    /**
     * A share of the table's width ("20%"), or px for an actions column.
     * Columns without one split what the others leave. The table always
     * fills its container; a cell's content is cut with an ellipsis rather
     * than widening its column.
     */
    width?: string;
    /** Keep the cell on one line, cut with an ellipsis when it overflows. */
    ellipsis?: boolean;
    /** Right-aligns the cells and the header, e.g. for a date or a number at the table's edge. */
    align?: "left" | "right";
    actions?: boolean;
    /** Offered in the toolbar's Filter menu; see DataTableFilterSpec. */
    filter?: DataTableFilterSpec<T>;
    /** Hidden until the user turns it on from the columns menu. */
    defaultHidden?: boolean;
    /** Not offered in the columns menu. The first column and an actions column never are. */
    alwaysVisible?: boolean;
}

/** A pre-defined filter of the rows: one segment of the toolbar's segmenter. */
export interface DataTableFilter<T> {
    key: string;
    label: string;
    /** The rows the filter keeps; omit for a filter that keeps every row ("All"). */
    match?: (row: T) => boolean;
    /** Shown when the filter keeps no row; the table's `emptyText` otherwise. */
    emptyText?: ReactNode;
    /**
     * The count on the segment: by default the rows the filter keeps. A number
     * for a count the caller knows (a server total); false for none, when the
     * server does the filtering and the rows loaded say nothing about the rest.
     */
    count?: number | false;
}

export interface DataTableProps<T> {
    columns: DataTableColumn<T>[];
    rows: T[];
    rowKey: (row: T) => string | number;
    /** False disables sorting and hides the arrows. */
    sortable?: boolean;
    /** The column the rows start sorted by, until a header is clicked. */
    defaultSort?: { key: string; direction?: DataTableSortDirection };
    /** Shown in place of the rows when there are none. */
    emptyText?: ReactNode;
    /** Shown when the filter or the search leaves no row. */
    noMatchText?: ReactNode;
    /**
     * The rows nested under a row. With it the table is a tree: a toggle in
     * the first column shows a row's children, indented under it, and sorting
     * applies among siblings. Rows start collapsed; the same toggle in the
     * first header expands or collapses them all.
     */
    childRows?: (row: T) => T[] | undefined;
    /** The caller's own controls (e.g. a view switch), first in the toolbar before the filters and the search box. */
    controls?: ReactNode;
    /**
     * Pre-defined filters of the (root) rows, shown as a segmenter in the
     * toolbar with the number of rows each keeps. The first one is selected
     * unless `defaultFilter` names another.
     */
    filters?: DataTableFilter<T>[];
    defaultFilter?: string;
    /** Called when the user picks a filter, e.g. to remember the choice. */
    onFilterChange?: (key: string) => void;
    /**
     * The texts a row is found by. With it a search box fills the toolbar: a
     * row shows when one of its texts contains the query, case-insensitive.
     * In a tree a row also shows when a descendant does, and the matching
     * branches open.
     */
    searchText?: (row: T) => string | (string | null | undefined)[];
    /**
     * A controlled search box instead, for a list the server filters: the box
     * only holds the text, the rows are whatever the caller passes.
     */
    search?: { value: string; onChange: (value: string) => void };
    searchPlaceholder?: string;
    /** Half the vertical padding in the cells, for a tighter list. */
    dense?: boolean;
    /** No header row, for a list whose columns need no naming. */
    hideHeader?: boolean;
    /** On a dark surface: transparent rows parted by rules, light text. */
    dark?: boolean;
    /**
     * With it, the column filters in force and the hidden columns are
     * remembered in localStorage, under this name, across visits.
     */
    storageKey?: string;
    className?: string;
}

function readStored<V>(key: string | undefined, fallback: V): V {
    if (!key) return fallback;
    try {
        const raw = localStorage.getItem(key);
        return raw ? (JSON.parse(raw) as V) : fallback;
    } catch {
        return fallback;
    }
}

function writeStored(key: string | undefined, value: unknown) {
    if (!key) return;
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // Storage full or blocked: the setting just does not survive a reload.
    }
}

interface SortState {
    key: string;
    direction: DataTableSortDirection;
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/** How far each tree level's first cell is indented under its parent, in px. */
const TREE_INDENT = 24;

/** A comparable value, or null for anything empty or unparsable. */
function comparable(
    raw: string | number | Date | null | undefined,
    type: DataTableSortType
): string | number | null {
    if (raw == null || raw === "") return null;
    if (type === "text") return String(raw).trim() || null;
    if (type === "date") {
        const time = raw instanceof Date ? raw.getTime() : Date.parse(String(raw));
        return Number.isFinite(time) ? time : null;
    }
    const number = typeof raw === "number" ? raw : Number(String(raw).replace(/[$,%\s]/g, ""));
    return Number.isFinite(number) ? number : null;
}

function sortRows<T>(
    rows: T[],
    column: DataTableColumn<T>,
    direction: DataTableSortDirection
): T[] {
    const type = column.sort ?? "text";
    const sign = direction === "ascending" ? 1 : -1;
    const value = column.sortValue ?? (() => null);
    return rows
        .map((row, order) => ({ row, order, value: comparable(value(row), type) }))
        .sort((a, b) => {
            // Empty values stay at the bottom in either direction.
            if (a.value === null) return b.value === null ? a.order - b.order : 1;
            if (b.value === null) return -1;
            const compared =
                type === "text"
                    ? collator.compare(a.value as string, b.value as string)
                    : (a.value as number) - (b.value as number);
            return compared * sign || a.order - b.order;
        })
        .map(({ row }) => row);
}

/**
 * A table with sortable column headers, an entity cell for the first column
 * and icon actions in the last one. Rows nest into a tree with `childRows`,
 * and a toolbar above holds the pre-defined filters and the search box.
 */
export function DataTable<T>({
    columns,
    rows,
    rowKey,
    sortable = true,
    defaultSort,
    emptyText,
    noMatchText = "Nothing matches",
    childRows,
    controls,
    filters,
    defaultFilter,
    onFilterChange,
    searchText,
    search,
    searchPlaceholder = "Search",
    dense,
    hideHeader,
    dark,
    storageKey,
    className,
}: DataTableProps<T>) {
    const [sort, setSort] = useState<SortState | undefined>(
        defaultSort && { key: defaultSort.key, direction: defaultSort.direction ?? "ascending" }
    );
    const [filterKey, setFilterKey] = useState(defaultFilter ?? filters?.[0]?.key);
    const [query, setQuery] = useState("");

    // Column filters and hidden columns, remembered under the storage key.
    const filtersStorage = storageKey && `datatable:${storageKey}:filters`;
    const columnsStorage = storageKey && `datatable:${storageKey}:columns`;
    const [columnFilters, setColumnFilters] = useState<DataTableActiveFilters>(() =>
        readStored(filtersStorage || undefined, {})
    );
    // The columns the user flipped away from their default, so a column that
    // is added later with its own default is not overridden by an old setting.
    const [toggledColumns, setToggledColumns] = useState<string[]>(() =>
        readStored(columnsStorage || undefined, [])
    );
    useEffect(
        () => writeStored(filtersStorage || undefined, columnFilters),
        [filtersStorage, columnFilters]
    );
    useEffect(
        () => writeStored(columnsStorage || undefined, toggledColumns),
        [columnsStorage, toggledColumns]
    );

    const setColumnFilter = (key: string, values: string[] | undefined) =>
        setColumnFilters((current) => {
            const next = { ...current };
            if (values) next[key] = values;
            else delete next[key];
            return next;
        });
    const toggleHidden = (key: string) =>
        setToggledColumns((current) =>
            current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
        );
    const isHidden = (c: DataTableColumn<T>) =>
        (c.defaultHidden ?? false) !== toggledColumns.includes(c.key);

    const filterable = columns.filter(
        (c): c is FilterableColumn<T> & DataTableColumn<T> => !!c.filter
    );
    // The first column (the entity) and an actions column always show.
    const hideable = columns.filter((c, index) => index > 0 && !c.actions && !c.alwaysVisible);
    const hidden = hideable.filter(isHidden).map((c) => c.key);
    const visibleColumns = columns.filter((c) => !hideable.includes(c) || !isHidden(c));
    // Whether rows are open unless toggled: closed, until the header toggle
    // flips it or a search opens the matching branches. The rows the user
    // toggled away from that state.
    const [allExpanded, setAllExpanded] = useState(false);
    const [toggled, setToggled] = useState<Set<string | number>>(() => new Set());

    const needle = query.trim().toLowerCase();
    useEffect(() => setToggled(new Set()), [needle]);

    const toggleAll = () => {
        setAllExpanded((current) => !current);
        setToggled(new Set());
    };

    const pickFilter = (key: string) => {
        setFilterKey(key);
        onFilterChange?.(key);
    };

    const filter = filters?.find((f) => f.key === filterKey) ?? filters?.[0];
    const segmentFiltered = filter?.match ? rows.filter(filter.match) : rows;
    // Column filters apply to the (root) rows, after the segmenter.
    const filtered =
        Object.keys(columnFilters).length > 0
            ? segmentFiltered.filter((row) => passesFilters(row, filterable, columnFilters))
            : segmentFiltered;

    // The rows the search shows: the matches, their ancestors and their
    // descendants. A row is marked when its own subtree holds a match.
    const matches = (row: T) => {
        const texts = searchText?.(row) ?? [];
        return (Array.isArray(texts) ? texts : [texts]).some((text) =>
            text?.toLowerCase().includes(needle)
        );
    };
    const shown = new Set<string | number>();
    const mark = (row: T, inherited: boolean): boolean => {
        const hit = inherited || matches(row);
        let kept = hit;
        for (const child of childRows?.(row) ?? []) kept = mark(child, hit) || kept;
        if (kept) shown.add(rowKey(row));
        return kept;
    };
    const searching = !!searchText && needle !== "";
    if (searching) filtered.forEach((row) => mark(row, false));
    const visible = (group: T[]) =>
        searching ? group.filter((row) => shown.has(rowKey(row))) : group;

    const sortColumn = sort && columns.find((c) => c.key === sort.key);
    const sorted = (group: T[]) =>
        sortColumn && sortable && sort ? sortRows(group, sortColumn, sort.direction) : group;

    const toggleExpanded = (key: string | number) =>
        setToggled((current) => {
            const next = new Set(current);
            if (!next.delete(key)) next.add(key);
            return next;
        });

    const cellClassName = (column: DataTableColumn<T>) =>
        clsx(
            column.align === "right" && classes["cell--right"],
            column.ellipsis && classes["cell--ellipsis"],
            column.actions && classes["cell--actions"]
        );

    // Each row, then its children while it is expanded, depth first.
    const renderRows = (group: T[], depth: number): ReactNode[] =>
        sorted(visible(group)).flatMap((row) => {
            const key = rowKey(row);
            const children = visible(childRows?.(row) ?? []);
            const open = toggled.has(key) !== (allExpanded || searching);
            const tr = (
                <tr key={key}>
                    {visibleColumns.map((column, index) => (
                        <td key={column.key} className={cellClassName(column)}>
                            {childRows && index === 0 ? (
                                <div
                                    className={classes.tree}
                                    style={{ paddingLeft: depth * TREE_INDENT }}
                                >
                                    {children.length > 0 ? (
                                        <button
                                            type="button"
                                            className={classes["tree-toggle"]}
                                            aria-expanded={open}
                                            aria-label={open ? "Collapse" : "Expand"}
                                            onClick={() => toggleExpanded(key)}
                                        >
                                            <SvgIcon
                                                name={open ? "down" : "right"}
                                                size={12}
                                                aria-hidden="true"
                                            />
                                        </button>
                                    ) : (
                                        <span className={classes["tree-toggle"]} />
                                    )}
                                    <div className={classes.tree__content}>
                                        {column.render(row)}
                                    </div>
                                </div>
                            ) : (
                                column.render(row)
                            )}
                        </td>
                    ))}
                </tr>
            );
            return open ? [tr, ...renderRows(children, depth + 1)] : [tr];
        });

    const toggleSort = (key: string) =>
        setSort((current) =>
            current?.key === key && current.direction === "ascending"
                ? { key, direction: "descending" }
                : { key, direction: "ascending" }
        );

    // Column widths reach the stylesheet as custom properties, one per column.
    const widths = Object.fromEntries(
        visibleColumns.map((column, index) => [
            `--column-${index + 1}-width`,
            column.width ?? "auto",
        ])
    ) as CSSProperties;

    const body = renderRows(filtered, 0);
    // What stands in for the rows when there are none to show.
    const placeholder =
        rows.length === 0
            ? emptyText
            : segmentFiltered.length === 0
              ? (filter?.emptyText ?? emptyText)
              : body.length === 0
                ? noMatchText
                : undefined;

    const table = (
        <table
            className={clsx(
                classes.table,
                !sortable && classes["table--static"],
                dense && classes["table--dense"],
                dark && classes["table--dark"],
                className
            )}
            style={widths}
        >
            {/* The widths live on the columns themselves: with the header
                hidden, a fixed layout would otherwise read them off the
                first row. */}
            <colgroup>
                {visibleColumns.map((column) => (
                    <col key={column.key} style={{ width: column.width }} />
                ))}
            </colgroup>
            {!hideHeader && (
                <thead>
                    <tr>
                        {visibleColumns.map((column, index) => {
                            const canSort = sortable && !!column.sort;
                            const direction = sort?.key === column.key ? sort.direction : undefined;
                            const title = column.sort ? (
                                <button
                                    type="button"
                                    className={classes["sort-button"]}
                                    disabled={!canSort}
                                    onClick={() => toggleSort(column.key)}
                                >
                                    {column.title}
                                    <SvgIcon
                                        name={direction === "ascending" ? "up" : "down"}
                                        size={10}
                                        className={classes["sort-indicator"]}
                                        aria-hidden="true"
                                    />
                                </button>
                            ) : (
                                column.title
                            );
                            return (
                                <th
                                    key={column.key}
                                    scope="col"
                                    aria-sort={canSort ? (direction ?? "none") : undefined}
                                    className={clsx(
                                        column.align === "right" && classes["cell--right"],
                                        column.actions && classes["cell--actions"]
                                    )}
                                >
                                    {childRows && index === 0 ? (
                                        // The whole tree's toggle, over the rows' own.
                                        <div className={classes.tree}>
                                            <button
                                                type="button"
                                                className={classes["tree-toggle"]}
                                                aria-expanded={allExpanded}
                                                aria-label={
                                                    allExpanded ? "Collapse all" : "Expand all"
                                                }
                                                onClick={toggleAll}
                                            >
                                                <SvgIcon
                                                    name={allExpanded ? "down" : "right"}
                                                    size={12}
                                                    aria-hidden="true"
                                                />
                                            </button>
                                            <div className={classes.tree__content}>{title}</div>
                                        </div>
                                    ) : (
                                        title
                                    )}
                                </th>
                            );
                        })}
                    </tr>
                </thead>
            )}
            <tbody>
                {body}
                {placeholder != null && body.length === 0 && (
                    <tr>
                        <td className={classes.empty} colSpan={visibleColumns.length}>
                            {placeholder}
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );

    const hasToolbar =
        controls ||
        !!filters?.length ||
        searchText ||
        search ||
        filterable.length > 0 ||
        hideable.length > 0;
    if (!hasToolbar) return table;
    return (
        <div>
            <div className={classes.toolbar}>
                {controls}
                {!!filters?.length && (
                    <Segmented
                        value={filter?.key}
                        onChange={(value) => pickFilter(String(value))}
                        options={filters.map((f) => ({
                            value: f.key,
                            label: (
                                <span className={classes.filter}>
                                    {f.label}
                                    {f.count !== false && (
                                        <span className={classes.filter__count}>
                                            {f.count ??
                                                (f.match
                                                    ? rows.filter(f.match).length
                                                    : rows.length)}
                                        </span>
                                    )}
                                </span>
                            ),
                        }))}
                    />
                )}
                {(searchText || search) && (
                    <SearchInput
                        className={classes.toolbar__search}
                        placeholder={searchPlaceholder}
                        value={search ? search.value : query}
                        onChange={(e) =>
                            search ? search.onChange(e.target.value) : setQuery(e.target.value)
                        }
                    />
                )}
                {(filterable.length > 0 || hideable.length > 0) && (
                    <div className={classes.toolbar__end}>
                        {filterable.length > 0 && (
                            <ColumnFilters
                                columns={filterable}
                                active={columnFilters}
                                onChange={setColumnFilter}
                            />
                        )}
                        {hideable.length > 0 && (
                            <ColumnsMenu
                                columns={hideable}
                                hidden={hidden}
                                onToggle={toggleHidden}
                            />
                        )}
                    </div>
                )}
            </div>
            {table}
        </div>
    );
}

DataTable.displayName = "DataTable";

export interface DataTableEntityCellProps {
    /** Square thumbnail; without one the fallback (typically an initial) shows instead. */
    imageSrc?: string;
    imageFallback?: ReactNode;
    /** Background behind the fallback (e.g. a color derived from the initial). */
    imageFallbackColor?: string;
    /** Main line, usually a link to the entity. */
    name: ReactNode;
    /** Secondary line under the name. */
    detail?: ReactNode;
    /** Small: a thumbnail that fits a two-line row, for rows mixed with plain text rows. */
    size?: "default" | "small";
    /** Large: the name a step up from the cell text, for the rows a list is about. */
    nameSize?: "default" | "large";
}

/** First-column cell presenting an entity: thumbnail, name and a detail line. */
export const DataTableEntityCell: FC<DataTableEntityCellProps> = ({
    imageSrc,
    imageFallback,
    imageFallbackColor,
    name,
    detail,
    size = "default",
    nameSize = "default",
}) => (
    <div className={clsx(classes.entity, size === "small" && classes["entity--small"])}>
        <div
            className={classes.entity__image}
            style={!imageSrc && imageFallbackColor ? { background: imageFallbackColor } : undefined}
        >
            {imageSrc ? <img alt="" src={imageSrc} /> : imageFallback}
        </div>
        <div className={classes.entity__info}>
            <span
                className={clsx(
                    classes.entity__name,
                    nameSize === "large" && classes["entity__name--large"]
                )}
            >
                {name}
            </span>
            {detail != null && <span className={classes.entity__detail}>{detail}</span>}
        </div>
    </div>
);

DataTableEntityCell.displayName = "DataTableEntityCell";

/** Last-column cell holding the row's action buttons. */
export const DataTableActions: FC<{ children?: ReactNode }> = ({ children }) => (
    <div className={classes.actions}>{children}</div>
);

DataTableActions.displayName = "DataTableActions";

export interface DataTableActionButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    /** SvgIcon name. */
    icon: string;
    /** Accessible name, also shown as the tooltip. */
    label: string;
}

export const DataTableActionButton: FC<DataTableActionButtonProps> = ({
    icon,
    label,
    className,
    disabled,
    ...restProps
}) => (
    <Tooltip title={label} mouseEnterDelay={0}>
        {/*
            The tooltip's target is this span, not the button: a disabled
            button gets no mouse events, and antd would otherwise wrap it
            in a span of its own and move the button's class onto that
            wrapper, leaving the button with the browser's default look.
        */}
        <span className={clsx(classes.action, disabled && classes["action--disabled"])}>
            <button
                type="button"
                className={clsx(classes["action-button"], className)}
                aria-label={label}
                disabled={disabled}
                {...restProps}
            >
                <SvgIcon
                    name={icon}
                    size={16}
                    className={classes["action-icon"]}
                    aria-hidden="true"
                />
            </button>
        </span>
    </Tooltip>
);

DataTableActionButton.displayName = "DataTableActionButton";
